import type { MouseEventHandler, PointerEventHandler } from "react"
import { format } from "date-fns"
import { ru, enUS } from "date-fns/locale"

// Use only for debugging
export const wait = async (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const normalizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === "string") {
    return error
  }
  try {
    return JSON.stringify(error)
  } catch {
    return String(error)
  }
}

export function copyObj<T extends Record<string, unknown>>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

export const isLast = (index: number, array: unknown[]) => {
  return index === array.length - 1
}

export function withStoppedClickPropagation(handler?: MouseEventHandler<HTMLElement>): MouseEventHandler<HTMLElement> {
  return (event) => {
    event.stopPropagation()
    handler?.(event)
  }
}

export function withStoppedPointerPropagation(
  handler?: PointerEventHandler<HTMLElement>
): PointerEventHandler<HTMLElement> {
  return (event) => {
    event.stopPropagation()
    handler?.(event)
  }
}

export const formatDate = (dateString?: string, locale?: string) => {
  if (!dateString) return ""

  const date = new Date(dateString)
  return format(date, "d MMMM yyyy, HH:mm:ss", { locale: locale === "ru" ? ru : enUS })
}

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) {
    return `${bytes} B`
  }

  const units = ["KB", "MB", "GB", "TB"]
  let value = bytes / 1024
  let unitIndex = 0

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }

  return `${value.toFixed(1)} ${units[unitIndex]}`
}

export async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text)
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

export function emptyStringsToUndefined(value: unknown): unknown {
  if (value === "") {
    return undefined
  }

  if (Array.isArray(value)) {
    return value.map((item) => emptyStringsToUndefined(item))
  }

  if (isObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, emptyStringsToUndefined(nestedValue)])
    )
  }

  return value
}

/**
 * Removes keys with null, undefined, or empty string values from an object.
 *
 * @param obj - The object to clean
 * @returns A new object with empty values removed
 */
export function removeEmptyValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value != null && value !== "")) as Partial<T>
}
